import { Button } from '@storeblocks/button';
import {
  Card,
  CardActions,
  CardHeadingGroup,
  CardImage,
} from '@storeblocks/card';
import { type FC } from 'react';
import { Link } from 'react-router';

import { NewsArticle } from '@/api/newsArticles/models/newsArticlesModels';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';

interface Props {
  newsArticle: NewsArticle;
}

/**
 * Card component for displaying a news article.
 * This component renders a smaller version of the news article card for use in a grid layout.
 *
 * The card is designed to handle single line headers and descriptions.
 * Since the Norwegian and English news articles have shorter headers and descriptions.
 */
export const NewsArticleCardSmall: FC<Props> = ({ newsArticle }) => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  return (
    <Card id={newsArticle.link}>
      <CardImage src={newsArticle.image} alt={newsArticle.imageAltText} />
      <CardHeadingGroup
        heading={newsArticle.header}
        description={newsArticle.description}
      />
      <CardActions>
        <Button
          size="medium"
          variant="primary"
          as={Link}
          target="_blank"
          to={newsArticle.link}
          data-trackid={getTrackId('read-article', TrackingElement.Link)}
        >
          {texts.news.articles.readArticleLink}
        </Button>
      </CardActions>
    </Card>
  );
};
